import type { ReactElement } from 'react';
import { CopyField } from './Modal';

/**
 * Shown after creating a client or rotating its token, with paste-ready install
 * instructions for the capture app operator. The token is also retained on the
 * client, so it can be re-displayed any time on the client's page.
 */
export function EnrollmentInstructions({
  clientName,
  enrollmentToken,
  rotated = false,
}: {
  clientName?: string;
  enrollmentToken: string;
  /** True when shown after a rotation (warns that the old token is now dead). */
  rotated?: boolean;
}): ReactElement {
  return (
    <div className="enrollment">
      <div className={rotated ? 'callout warn' : 'callout'}>
        {rotated ? (
          <>
            <strong>New token issued.</strong> The previous token no longer
            works — existing installs must re-enroll with this one. You can find
            it again any time on this client’s page.
          </>
        ) : (
          <>
            <strong>Copy this token to send to the operator.</strong> You can
            find it again any time on this client’s page — reuse the same token
            for every install.
          </>
        )}
      </div>

      <CopyField label="Enrollment token" value={enrollmentToken} />

      <div className="instructions">
        <h3>Install instructions{clientName ? ` for ${clientName}` : ''}</h3>
        <ol>
          <li>Send the operator the OpsRev Audit capture app installer.</li>
          <li>
            On first launch, paste the enrollment token above when prompted. The
            app registers the device and begins heartbeating automatically.
          </li>
          <li>
            Grant Screen Recording, Microphone, and Accessibility permissions
            when the OS asks.
          </li>
          <li>
            They record their day-to-day work; finished sessions upload to this
            console and appear under the client’s sessions.
          </li>
        </ol>
        <p className="muted">
          The token authenticates the device (HTTP <code>Authorization: Bearer
          &lt;token&gt;</code>). It maps every device and session back to this
          client.
        </p>
      </div>
    </div>
  );
}
